/**
 * Hook that provides instant client-side type compatibility and join path
 * validation by building lookup indexes from mapping metadata
 * (target_schema_snapshot, semantic_summaries, relationships).
 *
 * The `validateField()` and `validateJoinPath()` functions are synchronous
 * and can be called on every source field change for immediate feedback.
 */

import { useMemo, useCallback } from 'react';
import type { MappingResponse, ExpressionAnalysis } from '../../services/api';
import {
  evaluateFieldTypeCompatibility,
  TypeCompatibilityIssue,
} from '../validation/typeCompatibility';
import {
  buildJoinGraph,
  validateJoinPath as checkJoinPath,
  JoinPathWarning,
} from '../validation/joinPathValidator';
import { normalizeColumnsToArray } from '../graph/schemaResolution';

// ── Column metadata shapes ──────────────────────────────────────────

interface TargetColumnMeta {
  name: string;
  type: string;
  is_nullable: boolean;
  is_primary_key: boolean;
}

interface SourceColumnMeta {
  name: string;
  data_type: string;
  nullable: boolean;
  null_percentage?: number;
}

// ── Public types ────────────────────────────────────────────────────

export interface ValidateFieldParams {
  sourceFile: string;
  sourceField: string;
  targetTable: string;
  targetField: string;
  transformation?: string;
  transformationType?: string;
  /** From backend sqlglot analysis */
  expressionAnalysis?: ExpressionAnalysis;
}

export interface UseFieldValidationResult {
  /** Synchronous type compatibility check */
  validateField: (params: ValidateFieldParams) => TypeCompatibilityIssue[];
  /** Check if a source file has a valid join path to other sources used by the same target table */
  validateJoinPath: (sourceFile: string, targetTable: string) => JoinPathWarning | null;
  /** Look up target column metadata */
  getTargetColumn: (table: string, field: string) => TargetColumnMeta | undefined;
  /** Look up source column metadata */
  getSourceColumn: (file: string, field: string) => SourceColumnMeta | undefined;
}

// ── Hook ────────────────────────────────────────────────────────────

export function useFieldValidation(
  mappingResponse?: MappingResponse | null
): UseFieldValidationResult {
  // Build target column index: "table.column" -> TargetColumnMeta
  const targetIndex = useMemo(() => {
    const index = new Map<string, TargetColumnMeta>();
    const schema = mappingResponse?.metadata?.target_schema_snapshot;
    if (!schema?.tables) return index;

    for (const [tableName, tableDef] of Object.entries(schema.tables)) {
      for (const col of normalizeColumnsToArray(tableDef?.columns)) {
        if (!col?.name) continue;
        index.set(`${tableName}.${col.name}`, {
          name: col.name,
          type: (col.type as string) || (col.data_type as string) || '',
          is_nullable: (col.is_nullable as boolean | undefined) ?? (col.nullable as boolean | undefined) ?? true,
          is_primary_key: (col.is_primary_key as boolean | undefined) ?? (col.primary_key as boolean | undefined) ?? false,
        });
      }
    }
    return index;
  }, [mappingResponse?.metadata?.target_schema_snapshot]);

  // Build source column index: "file.column" -> SourceColumnMeta
  const sourceIndex = useMemo(() => {
    const index = new Map<string, SourceColumnMeta>();
    const summaries = mappingResponse?.metadata?.semantic_summaries;
    if (!summaries || typeof summaries !== 'object') return index;

    for (const [fileName, summary] of Object.entries(summaries as Record<string, Record<string, unknown>>)) {
      const columns = (summary as Record<string, unknown>)?.columns;
      if (!Array.isArray(columns)) continue;
      for (const col of columns) {
        if (!col?.name) continue;
        const nullable = col.nullable ?? col.is_nullable ?? true;
        const nullPct = col.null_percentage;
        index.set(`${fileName}.${col.name}`, {
          name: col.name,
          data_type: col.data_type || col.inferred_type || col.type || '',
          nullable: nullPct != null && nullPct > 0 ? true : nullable,
          null_percentage: nullPct,
        });
      }
    }
    return index;
  }, [mappingResponse?.metadata?.semantic_summaries]);

  // Build join graph from relationships
  const joinGraph = useMemo(
    () => buildJoinGraph(mappingResponse?.relationships ?? []),
    [mappingResponse?.relationships]
  );

  // Build source-files-per-target-table index
  const sourcesByTarget = useMemo(() => {
    const index = new Map<string, Set<string>>();
    if (!mappingResponse?.mappings) return index;
    for (const m of mappingResponse.mappings) {
      const tt = m.target_table?.trim();
      const sf = m.source_file?.trim();
      if (!tt || !sf) continue;
      if (!index.has(tt)) index.set(tt, new Set());
      index.get(tt)!.add(sf);
    }
    return index;
  }, [mappingResponse?.mappings]);

  const getTargetColumn = useCallback(
    (table: string, field: string) => targetIndex.get(`${table}.${field}`),
    [targetIndex]
  );

  const getSourceColumn = useCallback(
    (file: string, field: string) => sourceIndex.get(`${file}.${field}`),
    [sourceIndex]
  );

  const validateField = useCallback(
    (params: ValidateFieldParams): TypeCompatibilityIssue[] => {
      const { sourceFile, sourceField, targetTable, targetField, transformation, transformationType, expressionAnalysis } = params;

      const targetCol = targetIndex.get(`${targetTable}.${targetField}`);
      if (!targetCol) return [];

      const sourceCol = sourceIndex.get(`${sourceFile}.${sourceField}`);

      return evaluateFieldTypeCompatibility({
        sourceField,
        sourceType: sourceCol?.data_type,
        targetField,
        targetType: targetCol.type,
        sourceNullable: sourceCol?.nullable ?? true,
        targetNullable: targetCol.is_nullable,
        transformation,
        transformationType,
        expressionOutputType: expressionAnalysis?.cast_target_type || expressionAnalysis?.output_type_hint,
        hasCoalesce: expressionAnalysis?.has_coalesce,
      });
    },
    [targetIndex, sourceIndex]
  );

  const validateJoinPath = useCallback(
    (sourceFile: string, targetTable: string): JoinPathWarning | null => {
      const otherSources = sourcesByTarget.get(targetTable);
      if (!otherSources) return null;
      return checkJoinPath(sourceFile, Array.from(otherSources), joinGraph);
    },
    [sourcesByTarget, joinGraph]
  );

  return { validateField, validateJoinPath, getTargetColumn, getSourceColumn };
}
